/**
 * `?showcase=prologue` — launches any memory stage standalone over the showcase scene (keys 1–5 or
 * `&stage=<id>` to auto-start), so critics and the verification harness can screenshot each stage
 * without walking the whole prologue.
 */
import Phaser from 'phaser';
import { FONT_BODY, FONT_TITLE, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { runMemoryStage, stateFromContext } from '@/cinematics';
import { getDialogueBox } from '@/ui';
import { registerPrologueContent } from './register';
import { MEMORY_STAGE_IDS, type MemoryStageId } from './letter';
import { STAGES, isStageId } from './stages/defs';

export const PROLOGUE_SHOWCASE_KEY = 'prologue';

function prologueShowcase(scene: Phaser.Scene): void {
  registerPrologueContent();
  scene.cameras.main.setBackgroundColor('#1e2a3a');
  scene.add.text(GAME_W / 2, 48, '序章 · 回忆舞台', { fontFamily: FONT_TITLE, fontSize: '22px', color: '#e2c48c' }).setOrigin(0.5);
  scene.add.text(GAME_W / 2, 76, 'prologue showcase — 按数字键启动一个回忆；X/Esc 跳过', { fontFamily: FONT_BODY, fontSize: '11px', color: '#c9b89a' }).setOrigin(0.5);
  const lines = MEMORY_STAGE_IDS.map((id, i) => `${i + 1}  ${STAGES[id].title}   (${id})`);
  const menu = scene.add.text(GAME_W / 2, 118, lines.join('\n'), { fontFamily: FONT_BODY, fontSize: '14px', color: '#f1e3c2', lineSpacing: 8, align: 'left' }).setOrigin(0.5, 0);
  const status = scene.add.text(GAME_W / 2, GAME_H - 30, '', { fontFamily: FONT_BODY, fontSize: '12px', color: '#8ec95a' }).setOrigin(0.5);
  let busy = false;

  const launch = async (id: MemoryStageId): Promise<void> => {
    if (busy) return;
    busy = true;
    status.setText(`运行中：${STAGES[id].title}`);
    const ctx = ctxOf(scene);
    // Leave the Phaser step (key handlers / timers run inside SceneManager.update, where `scene.add`
    // is queued and returns null → runMemoryStage would report "could not register scene").
    await new Promise<void>((r) => setTimeout(r, 0));
    if (!scene.scene.isActive()) { busy = false; return; }
    try {
      const result = await runMemoryStage(scene, id, STAGES[id].intro, { state: stateFromContext(ctx), dialogueBox: getDialogueBox(scene), pauseCaller: true });
      status.setText(`${STAGES[id].title} → ${result}`);
      const ui = scene.scene.get('UI') as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
      if (ui?.fade && ui.scene.isActive()) await ui.fade(false, 300);
    } catch (e) {
      status.setText(`失败：${String(e)}`);
    } finally { busy = false; }
  };

  const kb = scene.input.keyboard;
  MEMORY_STAGE_IDS.forEach((id, i) => kb?.on(`keydown-${['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'][i]}`, () => void launch(id)));
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { kb?.removeAllListeners(); menu.destroy(); });

  const wanted = new URLSearchParams(location.search).get('stage');
  if (wanted && isStageId(wanted)) scene.time.delayedCall(200, () => void launch(wanted));
}

export function registerPrologueShowcase(): void {
  SHOWCASES[PROLOGUE_SHOWCASE_KEY] = prologueShowcase;
}
